import { Box, Card, CardContent, Typography, CircularProgress, Alert, Button, Stack } from '@mui/material';
import { ClearAll } from '@mui/icons-material';
import { ResponsiveBar } from '@nivo/bar';
import { useTheme } from '@mui/material/styles';
import { createNivoTheme, generateColorScheme } from '../../../lib/nivoTheme';
import { useRef, useState, useCallback, useMemo, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { useAdmissionMethodsStackedBarChart } from '../hooks/useAdmissionMethodsChart';
import { BaseChartControls, ChartDownloadMenu } from '../../../components/Charts';
import { useAppSelector } from '../../../store/hooks';
import { useChartExcelContext } from '../../../hooks';
import { formatExportTimestamp, type ChartExcelData } from '../../../utils/chartExcel';
import { stackedBarToRows } from '../../../utils/line-chart-excel-rows';

interface AdmissionMethodsBarChartProps {
	height?: number;
}

export function AdmissionMethodsBarChart({ height = 400 }: AdmissionMethodsBarChartProps) {
	const chartRef = useRef<HTMLDivElement>(null);
	const theme = useTheme();
	const reduxYears = useAppSelector((state) => state.filters.years);
	const [selectedYears, setSelectedYears] = useState<string[]>(reduxYears.map(y => String(y)));
	const [selectedEntities, setSelectedEntities] = useState<string[]>([]);

	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);

	const { nivoData, isLoading, error } = useAdmissionMethodsStackedBarChart({
		filters: {
			maKhoa: selectedFaculty ? [selectedFaculty] : undefined
		}
	});

	const filteredChartData = useMemo(() => {
		if (!nivoData) return null;

		let filteredData = nivoData.data;
		let filteredKeys = nivoData.keys;

		if (selectedYears.length > 0) {
			filteredData = filteredData.filter(item => selectedYears.includes(String(item.year)));
		}

		if (selectedEntities.length > 0) {
			filteredKeys = nivoData.keys.filter(key => selectedEntities.includes(key));
		}

		return { ...nivoData, data: filteredData, keys: filteredKeys };
	}, [nivoData, selectedEntities, selectedYears]);

	const handleClearFilters = useCallback(() => {
		setSelectedYears(reduxYears.map(y => String(y)));
		setSelectedEntities([]);
	}, [reduxYears]);

	const handleDownloadPNG = useCallback(async () => {
		if (!chartRef.current) return;

		try {
			const canvas = await html2canvas(chartRef.current);
			const link = document.createElement('a');
			link.download = `bieu-do-cot-phuong-thuc-tuyen-sinh-${formatExportTimestamp()}.png`;
			link.href = canvas.toDataURL();
			link.click();
		} catch (err) {
			console.error('Error downloading chart:', err);
		}
	}, []);

	const yearsLabel = useMemo(() => {
		return [...selectedYears]
			.map(Number)
			.sort((a, b) => a - b)
			.join(', ');
	}, [selectedYears]);

	const baseExcelContext = useChartExcelContext(yearsLabel || undefined);

	const excelContext = useMemo(() => ({
		...baseExcelContext,
		extra: {
			'Phương thức': filteredChartData ? filteredChartData.keys.join(', ') : '',
		},
	}), [baseExcelContext, filteredChartData]);

	const excelData: ChartExcelData | undefined = useMemo(() => {
		if (!filteredChartData || filteredChartData.data.length === 0 || filteredChartData.keys.length === 0) {
			return undefined;
		}
		if (selectedYears.length === 0) return undefined;

		return {
			sheetName: 'Phương thức xét tuyển',
			rows: stackedBarToRows(filteredChartData.data, filteredChartData.keys, filteredChartData.indexBy),
			context: excelContext,
		};
	}, [filteredChartData, selectedYears, excelContext]);

	const nivoTheme = useMemo(() => createNivoTheme(theme), [theme]);

	const availableYears = useMemo(() => {
		return nivoData?.filters?.years?.map(y => String(y)) || [];
	}, [nivoData]);

	const availableEntities = useMemo(() => {
		return nivoData?.filters?.entities || [];
	}, [nivoData]);

	const colors = useMemo(() => {
		if (!filteredChartData || !filteredChartData.keys || filteredChartData.keys.length === 0) return [];
		return generateColorScheme(filteredChartData.keys.length, theme);
	}, [filteredChartData, theme]);

	useEffect(() => {
		if (reduxYears.length > 0 && selectedYears.length === 0) {
			setSelectedYears(reduxYears.map(y => String(y)));
		}
	}, [reduxYears, selectedYears.length]);

	useEffect(() => {
		if (availableYears.length > 0 && selectedYears.length === 0) {
			setSelectedYears(availableYears);
		}
	}, [availableYears, selectedYears.length]);

	if (isLoading) {
		return (
			<Card>
				<CardContent>
					<Box display="flex" justifyContent="center" alignItems="center" height={200}>
						<CircularProgress />
					</Box>
				</CardContent>
			</Card>
		);
	}

	if (error) {
		return (
			<Card>
				<CardContent>
					<Alert severity="error">
						Có lỗi xảy ra khi tải dữ liệu biểu đồ
					</Alert>
				</CardContent>
			</Card>
		);
	}

	if (!nivoData || !nivoData.data || nivoData.data.length === 0) {
		return (
			<Card>
				<CardContent>
					<Alert severity="info">
						Chưa có dữ liệu
					</Alert>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardContent>
				<Box sx={{ mb: 3 }}>
					<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
						<Typography variant="h6" component="h2">
							Biểu đồ cột chồng theo Phương thức xét tuyển
						</Typography>
						<Stack direction="row" spacing={1}>
							<Button
								variant="outlined"
								size="small"
								startIcon={<ClearAll />}
								onClick={handleClearFilters}
							>
								Xóa bộ lọc
							</Button>
							<ChartDownloadMenu
								variant="button"
								onDownloadPng={handleDownloadPNG}
								excel={excelData}
								excelFilename="bieu-do-cot-phuong-thuc-tuyen-sinh"
							/>
						</Stack>
					</Stack>

					<BaseChartControls
						availableYears={availableYears}
						availableEntities={availableEntities}
						selectedYears={selectedYears}
						selectedEntities={selectedEntities}
						onYearsChange={setSelectedYears}
						onEntitiesChange={setSelectedEntities}
						entityLabel="Chọn phương thức"
						entityPlaceholder="Tất cả phương thức"
					/>
				</Box>

				<Box ref={chartRef} sx={{ width: '100%', height: height }}>
					{selectedYears.length > 0 && filteredChartData ? (
						<ResponsiveBar
							data={filteredChartData.data}
							keys={filteredChartData.keys}
							indexBy={filteredChartData.indexBy}
							margin={{ top: 60, right: 200, bottom: 60, left: 80 }}
							padding={0.3}
							valueScale={{ type: 'linear' }}
							indexScale={{ type: 'band', round: true }}
							colors={colors}
							groupMode="stacked"
							layout="vertical"
							borderRadius={4}
							borderColor={{
								from: 'color',
								modifiers: [['darker', 1.6]]
							}}
							axisTop={null}
							axisRight={null}
							axisBottom={{
								tickSize: 5,
								tickPadding: 5,
								tickRotation: 0,
								legend: 'Năm',
								legendPosition: 'middle',
								legendOffset: 50,
							}}
							axisLeft={{
								tickSize: 5,
								tickPadding: 5,
								tickRotation: 0,
								legend: 'Số lượng',
								legendPosition: 'middle',
								legendOffset: -70,
								format: (value) => Math.round(value).toString(),
							}}
							enableLabel={true}
							label={(d) => d.value !== null ? Math.round(d.value).toString() : '0'}
							labelSkipWidth={12}
							labelSkipHeight={12}
							labelTextColor={{
								from: 'color',
								modifiers: [['darker', 2]]
							}}
							enableTotals={true}
							totalsOffset={10}
							animate={false}
							legends={[
								{
									dataFrom: 'keys',
									anchor: 'right',
									direction: 'column',
									justify: false,
									translateX: 120,
									translateY: 0,
									itemsSpacing: 10,
									itemWidth: 100,
									itemHeight: 20,
									itemDirection: 'left-to-right',
									itemOpacity: 0.85,
									symbolSize: 12,
									symbolShape: 'circle',
								}
							]}
							theme={nivoTheme}
						/>
					) : (
						<Box display="flex" justifyContent="center" alignItems="center" height="100%">
							<Typography variant="body1" color="text.secondary">
								Vui lòng chọn năm để hiển thị biểu đồ
							</Typography>
						</Box>
					)}
				</Box>
			</CardContent>
		</Card>
	);
}
